"use client";

import dynamic from "next/dynamic";
import { useEffect, useMemo, useRef, useState } from "react";
import { playSound } from "@/lib/game/audio";
import {
  isConstellationComplete,
  pickConstellation,
  showConstellationVisualHint,
} from "@/lib/game/constellations";
import { formatStarReward } from "@/lib/game/stars";
import { ActivityLayout } from "./ActivityLayout";
import { ActivitySuccessOverlay } from "./ActivitySuccessOverlay";
import { DifficultyPicker } from "./DifficultyPicker";
import { GameIconButton } from "../game-ui";
import type { ConstellationSceneHandle } from "./PixiConstellation";
import { useGame } from "../GameProvider";

const PixiConstellation = dynamic(
  () => import("./PixiConstellation").then((m) => m.PixiConstellation),
  {
    ssr: false,
    loading: () => (
      <div className="flex h-full items-center justify-center bg-slate-950 text-sm text-slate-300">
        Loading constellation…
      </div>
    ),
  },
);

function ConstellationGame({
  challengeKey,
  onPlayAgain,
}: {
  challengeKey: number;
  onPlayAgain: () => void;
}) {
  const { completeActivity, progress } = useGame();
  const sceneRef = useRef<ConstellationSceneHandle | null>(null);
  const drawnRef = useRef<Set<string>>(new Set());
  const activityId = "constellation";
  const difficulty = progress.difficulty;
  const showVisualHint = showConstellationVisualHint(difficulty);

  const constellation = useMemo(
    () => pickConstellation(difficulty),
    [difficulty, challengeKey],
  );

  const [drawnCount, setDrawnCount] = useState(0);
  const totalEdges = constellation.edges.length;
  const [feedback, setFeedback] = useState<"idle" | "wrong" | "right">("idle");
  const completedRef = useRef(false);

  useEffect(() => {
    drawnRef.current = new Set();
    setDrawnCount(0);
    setFeedback("idle");
    completedRef.current = false;
  }, [constellation.id, challengeKey]);

  const handleEdgesChange = (drawn: Set<string>) => {
    if (feedback === "right") return;

    drawnRef.current = new Set(drawn);
    setDrawnCount(drawn.size);

    if (drawn.size !== totalEdges) {
      if (feedback === "wrong") setFeedback("idle");
      return;
    }

    if (isConstellationComplete(drawn, constellation.edges)) {
      if (completedRef.current) return;
      completedRef.current = true;
      setFeedback("right");
      playSound("correct");
      completeActivity(activityId, difficulty);
    } else if (feedback !== "wrong") {
      setFeedback("wrong");
      playSound("wrong");
    }
  };

  const handleClear = () => {
    if (feedback === "right") return;
    sceneRef.current?.clearLines();
    drawnRef.current = new Set();
    setDrawnCount(0);
    setFeedback("idle");
  };

  return (
    <div className="relative flex h-full min-h-0 w-full flex-col overflow-hidden bg-slate-950 text-white">
      <p className="shrink-0 py-2 text-center text-lg font-extrabold text-violet-200">
        {constellation.name}
      </p>

      <div className="min-h-0 flex-1 overflow-hidden">
        <PixiConstellation
          constellation={constellation}
          showVisualHint={showVisualHint}
          onSceneReady={(handle) => {
            sceneRef.current = handle;
          }}
          onEdgesChange={handleEdgesChange}
        />
      </div>

      <div className="shrink-0 space-y-2 border-t border-slate-700 bg-slate-900/90 px-3 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        <p className="text-center text-sm font-bold text-slate-300">
          {drawnCount} / {totalEdges}
        </p>

        {feedback === "wrong" && (
          <p className="text-center text-xs font-semibold text-rose-300">✗</p>
        )}

        {feedback !== "right" && (
          <div className="flex justify-center">
            <GameIconButton
              icon="clear"
              tone="slate"
              size="md"
              label="Clear lines"
              onClick={handleClear}
            />
          </div>
        )}
      </div>

      <ActivitySuccessOverlay
        show={feedback === "right"}
        title="Constellation complete!"
        subtitle={constellation.name}
        theme="slate"
        rewardLabel={formatStarReward(difficulty)}
        onPlayAgain={onPlayAgain}
      />
    </div>
  );
}

export function ConstellationActivity() {
  const { progress, setDifficulty, trackActivitySession } = useGame();
  const [challengeKey, setChallengeKey] = useState(0);

  useEffect(() => {
    trackActivitySession("constellation");
  }, [trackActivitySession]);

  const handleNewChallenge = () => setChallengeKey((k) => k + 1);

  return (
    <ActivityLayout
      title="Constellation Trace"
      headerExtra={
        <div className="flex items-center gap-2">
          <p className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Difficulty
          </p>
          <DifficultyPicker
            value={progress.difficulty}
            onChange={(level) => {
              setDifficulty(level);
              handleNewChallenge();
            }}
            compact
          />
        </div>
      }
    >
      <ConstellationGame
        key={`${progress.difficulty}-${challengeKey}`}
        challengeKey={challengeKey}
        onPlayAgain={handleNewChallenge}
      />
    </ActivityLayout>
  );
}
